import { Box, Card, CardContent, CardHeader, IconButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { QuickView } from "./components";
import { FansFilters } from "./components/FansFilters";
import { Fan, FansSorting } from "./models";
import { CardSearch } from "@arelia-components/ui-atoms/CardSearch";
import { useFansList as useFilteredFans } from "./hooks";
import EditAciton from "@arelia-components/ui-atoms/EditAciton";
import { FansEditDialog } from "./components/FansEditDialog";
import { API_ROUTE, fansColumnConfig } from "./constant";
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined'
import { useQueryClient } from "react-query";
import FansImportDialog from "./components/FansImportDialog";
import SettingsProcessSteps from "../settings/SettingsProcessSteps";
import { DataGrid } from "@arelia-components/ui-atoms/data-grid/DataGrid";
import { Inventory2Outlined, SearchOff } from "@mui/icons-material";
import { EmptyMessage } from "@arelia-components/ui-atoms/EmptyMessage";

type Props = {
  fans: Fan[];
}

export default function FansListPresenter({ fans }: Props) {
  const [activeFan, setActiveFan] = useState<Fan | null>(fans[0]);
  const [sortFansBy, setSortFansBy] = useState(FansSorting.createdDateDesc);
  const [editDialogOpened, setEditDialogOpened] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [importDialogOpened, setImportDialogOpened] = useState(false);

  const queryClient = useQueryClient();

  const downloadTemplate = () => {
    window.open('/api/my-fans/import/template', "_blank")
  };

  const startEdit = (event: any) => {
    event.stopPropagation();
    setEditDialogOpened(true);
  }

  const startImport = (event: any) => {
    event.stopPropagation();
    setImportDialogOpened(true);
  };

  const handleImportSuccess = () => {
    queryClient.invalidateQueries('useHqFans');
  };

  const filteredFans = useFilteredFans(fans, sortFansBy, searchValue);

  useEffect(() => {
    if (filteredFans.length === 0) {
      setActiveFan(null);
      return;
    }

    if (!activeFan || !fans.some(f => f.id === activeFan.id)) {
      setActiveFan(fans[fans.length - 1]);
    }
  }, [fans]);

  const displayedColumns = fansColumnConfig.filter(c =>
    ["fullName", "phone", "email", "createdAt"].includes(String(c.field))
  );

  return (
    <>
      <Box sx={styles.topContainer}>
        <FansFilters
          sortFansBy={sortFansBy}
          setSortFansBy={setSortFansBy}
        />
        <SettingsProcessSteps clickEnabled={true} />
      </Box>

      <Box sx={styles.listBox}>
        <Card sx={styles.listCard}>
          <CardHeader
            title="Fans"
            action={
              <>
                <CardSearch value={searchValue} onChange={setSearchValue} />
                <EditAciton editClicked={startEdit} />
                <IconButton onClick={startImport}>
                  <UploadFileOutlined />
                </IconButton>
              </>
            }
          />
          <CardContent>
            {fans.length === 0 ? (
              <EmptyMessage icon={Inventory2Outlined} message="No Data To Display" />
            ) : filteredFans.length === 0 ? (
              <EmptyMessage icon={SearchOff} message="No Data Match Search Request" />
            ) : (
              <DataGrid<Fan>
                rows={filteredFans}
                columns={displayedColumns}
                columnDefaults={{}}
                pageSize={10}
                rowsPerPage={[10, 25, 50]}
                onRowClick={(row) => setActiveFan(row)}
                getRowStyle={({ row }: { row: Fan }) => row?.id === activeFan?.id ? { backgroundColor: '#f5f5f5' } : {}}
                sx={{ height: "100%", minHeight: 200 }}
              />
            )}
          </CardContent>
        </Card>
        {activeFan && <Card sx={styles.detailsBox}>
          <CardHeader title="Quick view" />
          <CardContent>
            <QuickView activeFan={activeFan} />
          </CardContent>
        </Card>}
        {editDialogOpened && <FansEditDialog
          open={editDialogOpened}
          data={fans}
          apiRoute={API_ROUTE}
          columnConfig={fansColumnConfig}
          title={"My Fans"}
          onClose={() => setEditDialogOpened(false)}
        />}
        {importDialogOpened && <FansImportDialog
          onDownloadTemplate={downloadTemplate}
          onImported={handleImportSuccess}
          onClose={() => setImportDialogOpened(false)} />}
      </Box>
    </>
  );
}

const styles = {
  listBox: {
    display: "flex",
    gap: "22px",
    alignItems: "flex-start"
  },
  listCard: {
    flex: 1
  },
  detailsBox: {
    width: "356px",
    position: "sticky",
    top: 0
  },
  topContainer: {
    display: "flex",
    justifyContent: "space-between",
    cursor: 'pointer'
  }
}
